import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import { botGet, botPost, botDelete, backups as backupsApi, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { uid, slug, toMap, buildSyncBody } from "@/lib/structure";

// Hook condiviso dalle pagine Canali e Ruoli: costruisce lo stato editabile
// dalla cache del server, salva direttamente su Discord e gestisce i backup.
export function useStructure() {
  const { roles: rolesData, channels: channelsData, meta, refresh } = useData();
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loose, setLoose] = useState([]);
  // ID Discord degli elementi eliminati nell'editor: al salvataggio vengono cancellati davvero.
  const [deleted, setDeleted] = useState({ roles: [], channels: [] });
  const [baseline, setBaseline] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const build = useCallback((rolesArr, channelsArr, { markSaved = false } = {}) => {
    const r = rolesArr.map((x) => ({
      uid: uid(), id: x.id, name: x.name, color: x.color || "#99AAB5",
      hoist: !!x.hoist, mentionable: !!x.mentionable, permissions: x.permissions || [],
      isEveryone: !!x.isEveryone, position: x.position ?? 0,
    }));
    const cats = channelsArr.filter((c) => c.type === "category").sort((a, b) => a.position - b.position);
    const mkCh = (c) => ({
      uid: uid(), id: c.id, name: c.name, type: c.type, topic: c.topic || "",
      slowmode: c.rateLimitPerUser || 0, userLimit: c.userLimit || 0,
      syncWithCategory: false, overwrites: toMap(c.permissionOverwrites),
    });
    const cs = cats.map((cat) => ({
      uid: uid(), id: cat.id, name: cat.name, overwrites: toMap(cat.permissionOverwrites),
      channels: channelsArr.filter((c) => c.parentId === cat.id).sort((a, b) => a.position - b.position).map(mkCh),
    }));
    const ls = channelsArr.filter((c) => !c.parentId && c.type !== "category").sort((a, b) => a.position - b.position).map(mkCh);
    setRoles(r); setCategories(cs); setLoose(ls);
    setDeleted({ roles: [], channels: [] });
    if (markSaved) setBaseline(JSON.stringify(buildSyncBody({ roles: r, categories: cs, loose: ls })));
  }, []);

  const buildBody = () => buildSyncBody({ roles, categories, loose });
  const current = useMemo(() => JSON.stringify(buildSyncBody({ roles, categories, loose })), [roles, categories, loose]);
  const dirty = !!baseline && (current !== baseline || deleted.roles.length > 0 || deleted.channels.length > 0);

  // Quando i dati cambiano (salvataggio o modifica fatta su Discord) l'editor si aggiorna da solo,
  // tranne se ci sono modifiche non salvate: in quel caso si mostra un avviso invece di cancellarle.
  const dirtyRef = useRef(false);
  const ownSaveRef = useRef(false);
  const [remoteChanged, setRemoteChanged] = useState(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (!rolesData.length && !channelsData.length) return;
    if (dirtyRef.current && !ownSaveRef.current) { setRemoteChanged(true); return; }
    ownSaveRef.current = false;
    setRemoteChanged(false);
    build(rolesData, channelsData, { markSaved: true });
  }, [rolesData, channelsData, build]);

  // Salva direttamente su Discord: prima le eliminazioni, poi la sincronizzazione.
  const save = async () => {
    setSaving(true);
    const errors = [];
    try {
      for (const id of deleted.channels) {
        try { await botDelete(`channels/${id}`); } catch (e) { errors.push(apiError(e)); }
      }
      for (const id of deleted.roles) {
        try { await botDelete(`roles/${id}`); } catch (e) { errors.push(apiError(e)); }
      }
      const res = await botPost("sync", { ...buildBody(), options: { dryRun: false } });
      setDeleted({ roles: [], channels: [] });
      if (res?.queued) {
        // Bot offline: la modifica è in coda (l'avviso lo mostra DataContext). L'editor la considera salvata.
        setBaseline(current);
        return;
      }
      errors.push(...(res.errors || []).map((e) => `${e.name}: ${e.error}`));
      if (errors.length) toast.error(`Salvato con ${errors.length} problemi`, { description: errors.slice(0, 3).join(" · ") });
      else toast.success("Modifiche salvate su Discord");
      ownSaveRef.current = true;
      await refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const discard = () => { setRemoteChanged(false); build(rolesData, channelsData, { markSaved: true }); };

  const importSnapshot = async () => {
    setImporting(true);
    try { const snap = await botGet("guild/snapshot"); build(snap.roles || [], snap.channels || [], { markSaved: true }); toast.success("Struttura ricaricata dal server"); }
    catch (e) { toast.error(apiError(e)); } finally { setImporting(false); }
  };
  const exportBackup = async () => {
    try {
      const snap = await botGet("guild/snapshot");
      const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `discord-backup-${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup esportato");
    } catch (e) { toast.error(apiError(e)); }
  };
  const importFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        const rolesArr = data.roles || []; const channelsArr = data.channels || [];
        if (!rolesArr.length && !channelsArr.length) throw new Error("nessun ruolo o canale trovato");
        build(rolesArr, channelsArr);
        toast.success("Backup caricato — controlla e premi «Salva» per applicarlo");
      } catch (err) { toast.error("File non valido: " + err.message); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const markDeleted = (kind, ids) => {
    const list = ids.filter(Boolean);
    if (list.length) setDeleted((d) => ({ ...d, [kind]: [...new Set([...d[kind], ...list])] }));
  };

  // ── ruoli ──
  const updRole = (u, patch) => setRoles((a) => a.map((r) => r.uid === u ? { ...r, ...patch } : r));
  const addRole = () => {
    const r = { uid: uid(), name: "nuovo ruolo", color: "#99AAB5", hoist: false, mentionable: false, permissions: [] };
    setRoles((a) => [r, ...a]);
    return r.uid;
  };
  const delRole = (u) => {
    markDeleted("roles", [roles.find((r) => r.uid === u)?.id]);
    setRoles((a) => a.filter((r) => r.uid !== u));
  };

  // ── canali ──
  const addCategory = () => {
    const c = { uid: uid(), name: "NUOVA CATEGORIA", overwrites: {}, channels: [] };
    setCategories((a) => [...a, c]);
    return c.uid;
  };
  const addChannel = (catUid, type = "text") => {
    const ch = { uid: uid(), name: "nuovo-canale", type, topic: "", slowmode: 0, userLimit: 0, syncWithCategory: !!catUid, overwrites: {} };
    if (catUid) setCategories((a) => a.map((c) => c.uid === catUid ? { ...c, channels: [...c.channels, ch] } : c));
    else setLoose((a) => [...a, ch]);
    return ch.uid;
  };
  const updCategory = (catUid, patch) => setCategories((a) => a.map((c) => c.uid === catUid ? { ...c, ...patch } : c));
  const delCategory = (catUid) => {
    const cat = categories.find((c) => c.uid === catUid);
    if (cat) markDeleted("channels", [...cat.channels.map((ch) => ch.id), cat.id]);
    setCategories((a) => a.filter((c) => c.uid !== catUid));
  };
  const findChannel = (catUid, chUid) => (catUid ? categories.find((c) => c.uid === catUid)?.channels : loose)?.find((ch) => ch.uid === chUid);
  const updChannel = (catUid, chUid, patch) => {
    if (catUid) setCategories((a) => a.map((c) => c.uid === catUid ? { ...c, channels: c.channels.map((ch) => ch.uid === chUid ? { ...ch, ...patch } : ch) } : c));
    else setLoose((a) => a.map((ch) => ch.uid === chUid ? { ...ch, ...patch } : ch));
  };
  const delChannel = (catUid, chUid) => {
    markDeleted("channels", [findChannel(catUid, chUid)?.id]);
    if (catUid) setCategories((a) => a.map((c) => c.uid === catUid ? { ...c, channels: c.channels.filter((ch) => ch.uid !== chUid) } : c));
    else setLoose((a) => a.filter((ch) => ch.uid !== chUid));
  };
  // Sposta un canale in un'altra categoria (null = fuori dalle categorie), prima di beforeUid o in fondo.
  const moveChannel = (fromCat, chUid, toCat, beforeUid = null) => {
    const ch = findChannel(fromCat, chUid);
    if (!ch || chUid === beforeUid) return;
    const moved = { ...ch, syncWithCategory: toCat ? ch.syncWithCategory : false };
    const insert = (list) => {
      const rest = list.filter((x) => x.uid !== chUid);
      const i = beforeUid ? rest.findIndex((x) => x.uid === beforeUid) : -1;
      if (i === -1) return [...rest, moved];
      return [...rest.slice(0, i), moved, ...rest.slice(i)];
    };
    const without = (list) => list.filter((x) => x.uid !== chUid);
    setCategories((a) => a.map((c) => {
      let chs = c.channels;
      if (c.uid === fromCat) chs = without(chs);
      if (c.uid === toCat) chs = insert(chs);
      return chs === c.channels ? c : { ...c, channels: chs };
    }));
    setLoose((a) => {
      let l = a;
      if (!fromCat) l = without(l);
      if (!toCat) l = insert(l);
      return l;
    });
  };
  const moveCategory = (catUid, beforeUid) => setCategories((a) => {
    const c = a.find((x) => x.uid === catUid);
    if (!c || catUid === beforeUid) return a;
    const rest = a.filter((x) => x.uid !== catUid);
    const i = beforeUid ? rest.findIndex((x) => x.uid === beforeUid) : -1;
    return i === -1 ? [...rest, c] : [...rest.slice(0, i), c, ...rest.slice(i)];
  });

  return {
    meta, roles, categories, loose, saving, importing, fileRef, dirty, remoteChanged,
    setRoles, setCategories, setLoose, build, buildBody,
    save, discard, importSnapshot, exportBackup, importFile,
    updRole, addRole, delRole,
    addCategory, addChannel, updCategory, delCategory, updChannel, delChannel, moveChannel, moveCategory,
    backupsApi, slug,
  };
}
